import {Component, inject, Input} from '@angular/core';
import {ColDef, GridApi, GridReadyEvent, RowClickedEvent} from "ag-grid-community";
import {FormatCurrencyPipe} from "../../../pipes/format-currency.pipe";
import {AsyncPipe, DatePipe} from "@angular/common";
import {ErrorHandlingService} from "../../../services/error-handling.service";
import {AgGridAngular} from "ag-grid-angular";
import {LoadingCardContentComponent} from "../../../shared/loading-card-content/loading-card-content.component";
import {UIStateEnum} from "../../../enum/UIStateEnum";
import {ICharityPayout} from "../../../interfaces/charity/ICharityPayout";
import {CharityPayoutService} from "../../../services/charity-payout.service";
import {BehaviorSubject, Observable} from "rxjs";
import {IDropdownOption} from "../../../interfaces/IDropDownOptions";
import {CharityService} from "../../../services/charity.service";
import {FilterableDropdownComponent} from "../../../components/filterable-dropdown/filterable-dropdown.component";
import {MatCard, MatCardContent, MatCardHeader, MatCardTitle} from "@angular/material/card";
import {IGameQueryResult} from "../../../interfaces/IGameQueryResult";
import {PipesModule} from "../../../pipes/pipes.module";
import {ICharityBalance} from "../../../interfaces/charity/ICharityBalance";
import {MatDialog} from "@angular/material/dialog";
import {CharityPayoutDetailsComponent} from "../../../components/dialogs/charity-payout-details/charity-payout-details.component";

@Component({
  selector: 'app-view-charity-payouts',
  standalone: true,
  templateUrl: './view-charity-payouts.component.html',
  imports: [
    AgGridAngular,
    LoadingCardContentComponent,
    AsyncPipe,
    FilterableDropdownComponent,
    MatCard,
    MatCardHeader,
    MatCardTitle,
    MatCardContent,
    PipesModule
  ],
  styleUrls: ['./view-charity-payouts.component.scss']
})
export class ViewCharityPayoutsComponent {

  private currencyPipe: FormatCurrencyPipe = inject(FormatCurrencyPipe);
  private charityPayoutService: CharityPayoutService = inject(CharityPayoutService);
  private charityService: CharityService = inject(CharityService);
  private errorHandlingService: ErrorHandlingService = inject(ErrorHandlingService);
  private datePipe: DatePipe = inject(DatePipe);
  private dialog: MatDialog = inject(MatDialog);

  @Input() set gameData(gameP: IGameQueryResult) {
    // Reset state when game changes
    if (this.activeGame && this.activeGame.Id !== gameP.Id) {
      this.resetViewState();
    }
    this.activeGame = gameP;
    this.fetchCharitiesForGame();
  }

  private resetViewState(): void {
    this.charityPayouts.next([]);
    this.charityBalance = null;
    this.totalPayoutAmount = 0;
    this.selectedCharityName = '';
  }

  public charityPayouts: BehaviorSubject<ICharityPayout[]> = new BehaviorSubject<ICharityPayout[]>([]);
  public charityPayouts$: Observable<ICharityPayout[]> = this.charityPayouts.asObservable();
  public payoutsGridApi!: GridApi<ICharityPayout>;
  public activeGame: IGameQueryResult | null = null;
  public charityOptions: IDropdownOption[] = [];
  public totalPayoutAmount: number = 0;
  public charityBalance: ICharityBalance | null = null;
  private selectedCharityName: string = '';

  uiState = UIStateEnum.ShowData;
  protected readonly UIStateEnum = UIStateEnum;

  public payoutColDefs: ColDef[] = [
    {
      headerName: 'Payout Date',
      field: 'CreatedOnDate',
      resizable: true,
      valueFormatter: params => {
        const convertedDate = this.datePipe.transform(params.value, 'MMM d, y, h:mm a');
        return convertedDate ? convertedDate : '';
      },
      sortable: true,
      sort: 'desc',
      flex: 1
    },
    {
      headerName: 'Payout Amount',
      resizable: true,
      wrapHeaderText: true,
      sortable: true,
      field: 'PayoutAmount',
      valueFormatter: params => {
        return this.currencyPipe.transform(params.value);
      },
      flex: 1
    },
    {
      headerName: 'Last Updated',
      field: 'LastUpdatedDate',
      resizable: true,
      valueFormatter: params => {
        const convertedDate = this.datePipe.transform(params.value, 'MMM d, y, h:mm a');
        return convertedDate ? convertedDate : '';
      },
      sortable: true,
      flex: 1
    }
  ];

  fetchCharitiesForGame(): void {
    if (!this.activeGame) return;

    this.uiState = UIStateEnum.ShowLoading;
    this.charityService.fetchCharitiesPerGameByCategory(this.activeGame.Id).subscribe({
      next: (charitiesByCategory) => {
        const allCharities = charitiesByCategory.flatMap(category => category.Charities);
        this.charityOptions = allCharities.map(charity => ({
          id: charity.Id,
          name: charity.Name
        }));
        this.uiState = UIStateEnum.ShowData;
      },
      error: (err) => {
        this.uiState = UIStateEnum.ShowData;
        this.errorHandlingService.displayPageLevelErrorMessage(err);
      }
    });
  }

  onCharitySelected(charityId: string): void {
    this.uiState = UIStateEnum.ShowLoading;

    // Store the selected charity name
    const selectedCharity = this.charityOptions.find(option => option.id === charityId);
    this.selectedCharityName = selectedCharity?.name || '';

    // Fetch both payouts and balance
    this.charityPayoutService.fetchCharityPayoutsByCharityId(charityId, this.activeGame?.Id).subscribe({
      next: (payouts) => {
        this.charityPayouts.next(payouts);
        this.calculateTotalPayoutAmount(payouts);
      },
      error: (err) => {
        this.uiState = UIStateEnum.ShowData;
        this.errorHandlingService.displayPageLevelErrorMessage(err);
      }
    });

    this.charityPayoutService.fetchBalanceByCharityId(charityId, this.activeGame?.Id).subscribe({
      next: (balance) => {
        this.charityBalance = balance;
        this.uiState = UIStateEnum.ShowData;
      },
      error: (err) => {
        this.uiState = UIStateEnum.ShowData;
        this.errorHandlingService.displayPageLevelErrorMessage(err);
      }
    });
  }

  onRowClicked(event: RowClickedEvent<ICharityPayout>): void {
    if (event.data) {
      this.dialog.open(CharityPayoutDetailsComponent, {
        data: {
          payout: event.data,
          charityName: this.selectedCharityName,
          gameId: this.activeGame?.Id
        },
        width: '600px'
      });
    }
  }

  calculateTotalPayoutAmount(payouts: ICharityPayout[]): void {
    this.totalPayoutAmount = payouts.reduce((sum, payout) => sum + payout.PayoutAmount, 0);
  }

  onPayoutsGridReady(params: GridReadyEvent<ICharityPayout>) {
    this.payoutsGridApi = params.api;
    this.payoutsGridApi.sizeColumnsToFit({
      defaultMinWidth: 90,
    });
  }

  protected readonly uiStateForTemplate = UIStateEnum;
}
